import { Fragment, useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import Select from "react-select"
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../../services/AxiosInstance";
import logo from "../../../assets/images/logo.png";
import { showErrorAlert , handleSuccess } from "../../../services/ErrorHandlingService";
import Loader from "../Common/Loader";
import { getInternalType } from "../../../utils/questionTypeUtils";
import FormAlreadySubmitted from "../Common/FormAlreadySubmitted";
import FormSuccessMessage from "../Common/FormSuccessMessage";
import GapldFormFields from "./GapldFormFields";
import MaleFormFields from "./MaleFormFields";
import MenopauseFormFields from "./MenopauseFormFields";

// Helper function to generate unique IDs for option questions
const generateUniqueOptionQuestionId = (questionId, option, optionQuestion, optionIndex) => {
  const optionId = option.option_id || option.id || optionIndex;
  const questionIdForUnique = optionQuestion.id || optionQuestion.question_id || optionIndex;
  return `${questionId}_${optionId}_${questionIdForUnique}`;
};

// Helper function to convert file to base64
const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const PatientForm = () => {
  const { formId: encodedFormId } = useParams();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [responses, setResponses] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { patientId: encodedPatientId } = useParams();
  const [menopauseData, setMenopauseData] = useState({
    menopause_date: "",
    age_first_m: "",
    menstruation: "",
    how_long_m_stopped: "",
    how_long_m_stopped_unit: "Month",
    causes_m: "",
    other_name: "",
    how_many_days_occur: "",
  });
  const [gapldData, setGapldData] = useState({
    pregnancy_status: "",
    marriage_info_id: "0",
    abortion_date: "",
    which_rx_helped: "",
    how_did_miscarriage_happen: "",
    miscarriage_reason: "",
    delivery_date: "",
    How_was_the_delivery: "",
    gender: "",
  });
  const [maleData, setMaleData] = useState({
    date: "",
    symptoms: null,
    otherSymptom: "",
    metallicColor: null,
  });
  // Add state to track selected options for option questions
  const [selectedOptions, setSelectedOptions] = useState({});
  const [visibleOptionQuestions, setVisibleOptionQuestions] = useState({});

  const [partnerInfo, setPartnerInfo] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    email: "",
    mobile_no: "",
    alternate_mobile_no: "",
    date_of_birth: "",
    gender: ""
  });

  const [partnerErrors, setPartnerErrors] = useState({})
  const handlePartnerChange = (e) => {
    let { name, value } = e.target;

    // Only digits for phone
    if (name === "mobile_no" || name === "alternate_mobile_no") {
      value = value.replace(/\D/g, "");
    }

    setPartnerInfo((prev) => ({
      ...prev,
      [name]: value,
    }));

    // ✅ Live error remove / validate
    setPartnerErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };

      switch (name) {
        case "first_name":
          if (!value.trim()) {
            updatedErrors.first_name = "First name is required";
          } else if (value.trim().length < 2) {
            updatedErrors.first_name = "Minimum 2 characters required";
          } else if (!/^[a-zA-Z\s]+$/.test(value)) {
            updatedErrors.first_name = "Only letters allowed";
          } else {
            delete updatedErrors.first_name;
          }
          break;

        case "middle_name":
          if (value && !/^[a-zA-Z\s]+$/.test(value)) {
            updatedErrors.middle_name = "Only letters allowed";
          } else {
            delete updatedErrors.middle_name;
          }
          break;

        case "last_name":
          if (!value.trim()) {
            updatedErrors.last_name = "Last name is required";
          } else if (value.trim().length < 2) {
            updatedErrors.last_name = "Minimum 2 characters required";
          } else if (!/^[a-zA-Z\s]+$/.test(value)) {
            updatedErrors.last_name = "Only letters allowed";
          } else {
            delete updatedErrors.last_name;
          }
          break;

        case "email":
          if (!value.trim()) {
            updatedErrors.email = "Email is required";
          } else if (!validateEmail(value)) {
            updatedErrors.email = "Invalid email";
          } else {
            delete updatedErrors.email;
          }
          break;

        case "mobile_no":
          if (!value.trim()) {
            updatedErrors.mobile_no = "Mobile number is required";
          } else if (!validatePhone(value)) {
            updatedErrors.mobile_no = "Enter valid 10-digit number";
          } else {
            delete updatedErrors.mobile_no;
          }
          break;

        case "alternate_mobile_no":
          if (value && !validatePhone(value)) {
            updatedErrors.alternate_mobile_no = "Invalid number";
          } else {
            delete updatedErrors.alternate_mobile_no;
          }
          break;

        case "date_of_birth":
          if (!value) {
            updatedErrors.date_of_birth = "Date of birth is required";
          } else {
            delete updatedErrors.date_of_birth;
          }
          break;

        default:
          break;
      }

      return updatedErrors;
    });
  };

  const handleGenderChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";

    setPartnerInfo((prev) => ({
      ...prev,
      gender: value,
    }));

    // ✅ validation
    setPartnerErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };

      if (!value) {
        updatedErrors.gender = "Gender is required";
      } else {
        delete updatedErrors.gender;
      }

      return updatedErrors;
    });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]{10}$/
    return phoneRegex.test(phone.replace(/\D/g, ""))
  }
  const validatePartnerInfo = () => {
    const newErrors = {}

    // First Name
    if (!partnerInfo.first_name.trim()) {
      newErrors.first_name = "First name is required"
    } else if (partnerInfo.first_name.trim().length < 2) {
      newErrors.first_name = "First name must be at least 2 characters"
    } else if (!/^[a-zA-Z\s]+$/.test(partnerInfo.first_name.trim())) {
      newErrors.first_name = "First name should contain only letters"
    }

    // Middle Name (optional)
    if (partnerInfo.middle_name.trim()) {
      if (!/^[a-zA-Z\s]+$/.test(partnerInfo.middle_name.trim())) {
        newErrors.middle_name = "Middle name should contain only letters"
      }
    }

    // Last Name
    if (!partnerInfo.last_name.trim()) {
      newErrors.last_name = "Last name is required"
    } else if (partnerInfo.last_name.trim().length < 2) {
      newErrors.last_name = "Last name must be at least 2 characters"
    } else if (!/^[a-zA-Z\s]+$/.test(partnerInfo.last_name.trim())) {
      newErrors.last_name = "Last name should contain only letters"
    }

    // Email (optional)
    if (partnerInfo.email.trim()) {
      if (!validateEmail(partnerInfo.email)) {
        newErrors.email = "Please enter a valid email address"
      }
    }

    // Mobile No
    if (!partnerInfo.mobile_no.trim()) {
      newErrors.mobile_no = "Mobile number is required"
    } else if (!validatePhone(partnerInfo.mobile_no)) {
      newErrors.mobile_no = "Please enter a valid 10-digit phone number"
    }

    // Alternate Mobile (optional)
    if (partnerInfo.alternate_mobile_no.trim()) {
      if (!validatePhone(partnerInfo.alternate_mobile_no)) {
        newErrors.alternate_mobile_no = "Please enter a valid 10-digit phone number"
      }
    }

    // DOB
    if (!partnerInfo.date_of_birth) {
      newErrors.date_of_birth = "Date of birth is required"
    }

    if (!partnerInfo.gender) {
      newErrors.gender = "Gender is required";
    }
    setPartnerErrors(newErrors)

    return Object.keys(newErrors).length === 0
  }


  // Refs for time inputs to handle click outside
  const timeInputRefs = useRef({});

  // Decode base64 formId
  const formId = useMemo(() => {
    try {
      if (!encodedFormId || encodedFormId.trim() === '') {
        return null;
      }

      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(encodedFormId)) {
        return null;
      }

      return atob(encodedFormId);
    } catch (error) {
      return null;
    }
  }, [encodedFormId]);

  // Decode base64 patientId
  const patientId = useMemo(() => {
    try {
      if (!encodedPatientId || encodedPatientId.trim() === '') {
        return null;
      }

      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(encodedPatientId)) {
        return null;
      }

      return atob(encodedPatientId);
    } catch (error) {
      return null;
    }
  }, [encodedPatientId]);

  useEffect(() => {
    const submittedFlag = localStorage.getItem(`form_submitted_${formId}_${patientId}`);
    if (submittedFlag === "true") {
      setIsSubmitted(true);
      setLoading(false);
      return;
    }

    if (!formId) {
      setError("Invalid form ID");
      setLoading(false);
      return;
    }

    fetchFormData();
  }, [formId, patientId]);

  // Update visible option questions when formData changes
  useEffect(() => {
    if (formData) {
      updateVisibleOptionQuestions(responses, formData);
    }
  }, [formData, responses]);

  // Handle click outside for time inputs
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.values(timeInputRefs.current).forEach(ref => {
        if (ref && !ref.contains(event.target)) {
          // Close modal, dropdown, etc., if needed
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchFormData = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axiosInstance.post(`/admin/formDetails`, {
        form_id: formId,
        patient_id: patientId
      });

      if (response.data.status === "success") {
        const data = response.data.data;

        // Initialize responses from existing answers
        const initialResponses = {};
        const initialSelectedOptions = {};

        // Handle answers for all questions (both standalone and grouped) in sequence
        (data.questions || []).forEach((item, itemIndex) => {
          if (!item.isGroup || item.isGroup === "No") {
            // Handle standalone question
            if (item.answer) {
              const isCheckbox =
                getInternalType(item.question_type) === "checkbox";
              if (isCheckbox && typeof item.answer === "string") {
                // Convert comma-separated string to array for checkboxes
                initialResponses[item.form_question_id] = item.answer
                  .split(", ")
                  .filter(Boolean);
              } else {
                initialResponses[item.form_question_id] = item.answer;
              }
            }

            // Initialize selected options for standalone questions
            if (item.options && item.options.length > 0) {
              item.options.forEach((option) => {
                if (option.option_questions && option.option_questions.length > 0) {
                  const optionKey = `${item.form_question_id}_${option.option_id}`;
                  initialSelectedOptions[optionKey] = false;
                }
              });
            }
          } else {
            // Handle grouped questions
            (item.questions || []).forEach((question) => {
              if (question.answer) {
                const isCheckbox =
                  getInternalType(question.question_type) === "checkbox";
                if (isCheckbox && typeof question.answer === "string") {
                  // Convert comma-separated string to array for checkboxes
                  initialResponses[question.form_question_id] = question.answer
                    .split(", ")
                    .filter(Boolean);
                } else {
                  initialResponses[question.form_question_id] = question.answer;
                }
              }

              // Initialize selected options for option questions
              if (question.options && question.options.length > 0) {
                question.options.forEach((option) => {
                  if (option.option_questions && option.option_questions.length > 0) {
                    const optionKey = `${question.form_question_id}_${option.option_id}`;
                    initialSelectedOptions[optionKey] = false;
                  }
                });
              }
            });
          }
        });

        setFormData(data);
        setResponses(initialResponses);
        setSelectedOptions(initialSelectedOptions);

        // Initialize visible option questions based on existing answers
        updateVisibleOptionQuestions(initialResponses, data);
      } else {
        throw new Error("Form not found");
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.messages?.error || "Error loading form";
      setError(errorMessage);
      showErrorAlert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Function to update visible option questions based on selected options
  const updateVisibleOptionQuestions = (currentResponses, formData) => {
    const newVisibleQuestions = {};

    // Process all questions in sequence (both standalone and grouped)
    (formData.questions || []).forEach((item) => {
      if (item.isGroup === "Yes" && item.questions) {
        // Handle grouped questions
        item.questions.forEach((question) => {
          if (question.options && question.options.length > 0) {
            question.options.forEach((option) => {
              if (option.option_questions && option.option_questions.length > 0) {
                const optionKey = `${question.form_question_id}_${option.option_id}`;
                const isSelected = checkIfOptionIsSelected(question, option, currentResponses[question.form_question_id]);
                newVisibleQuestions[optionKey] = isSelected;
              }
            });
          }
        });
      } else {
        // Handle standalone questions
        if (item.options && item.options.length > 0) {
          item.options.forEach((option) => {
            if (option.option_questions && option.option_questions.length > 0) {
              const optionKey = `${item.form_question_id}_${option.option_id}`;
              const isSelected = checkIfOptionIsSelected(item, option, currentResponses[item.form_question_id]);
              newVisibleQuestions[optionKey] = isSelected;
            }
          });
        }
      }
    });

    setVisibleOptionQuestions(newVisibleQuestions);
  };

  // Helper function to check if an option is selected
  const checkIfOptionIsSelected = (question, option, currentValue) => {
    if (!currentValue) return false;

    const questionType = getInternalType(question.question_type);

    if (questionType === "checkbox" || questionType === "multiple_selection") {
      const optionText = option.label || option.option_name;
      return Array.isArray(currentValue) && currentValue.includes(optionText);
    } else {
      const optionText = option.label || option.option_name;
      return currentValue === optionText;
    }
  };

  const handleChange = (questionId, value, optionId = null) => {
    setResponses((prev) => {
      const newResponses = {
        ...prev,
        [questionId]: value,
        ...(optionId !== null && { [`${questionId}_option_id`]: optionId }),
      };

      // Clear error for this question when user makes a change
      if (formErrors[questionId]) {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[questionId];
          return newErrors;
        });
      }

      // Update visible option questions based on the new selection
      updateVisibleOptionQuestions(newResponses, formData);

      return newResponses;
    });
  };

  const validateForm = () => {
    const errors = {};

    const validateQuestion = (question) => {
      const response = responses[question.form_question_id];
      const internalType = getInternalType(question.question_type);
      const isCheckbox = internalType === "checkbox";

      // ✅ FILE INPUT VALIDATION
      if (
        question.question_type === "File Input" ||
        internalType === "file"
      ) {
        const file = response;

        if (question.is_required === "Yes" && !file) {
          errors[question.form_question_id] = "File is required";
          return;
        }

        if (file instanceof File) {
          // Validate allowed file types
          if (question.allowed_file_type) {
            const allowedTypes = question.allowed_file_type
              .split(",")
              .map((t) => t.trim().toLowerCase());

            const fileExt = file.name.split(".").pop().toLowerCase();

            if (!allowedTypes.includes(fileExt)) {
              errors[question.form_question_id] =
                `Allowed file types: ${question.allowed_file_type}`;
            }
          }

          // Validate file size
          if (question.max_file_size_mb) {
            const maxSize = question.max_file_size_mb * 1024 * 1024;

            if (file.size > maxSize) {
              errors[question.form_question_id] =
                `File must be under ${question.max_file_size_mb} MB`;
            }
          }
        }

        return;
      }

      // ✅ NORMAL REQUIRED VALIDATION
      if (question.is_required === "Yes") {
        if (isCheckbox) {
          if (!Array.isArray(response) || response.length === 0) {
            errors[question.form_question_id] = "This field is required";
          }
        } else {
          if (!response || response.toString().trim() === "") {
            errors[question.form_question_id] = "This field is required";
          }
        }
      }

      // ✅ OPTION QUESTION VALIDATION
      if (question.options && question.options.length > 0) {
        question.options.forEach((option) => {
          if (option.option_questions && option.option_questions.length > 0) {
            const optionKey = `${question.form_question_id}_${option.option_id}`;

            if (visibleOptionQuestions[optionKey]) {
              option.option_questions.forEach((optionQuestion, optionIdx) => {
                const uniqueOptionQuestionId =
                  generateUniqueOptionQuestionId(
                    question.form_question_id,
                    option,
                    optionQuestion,
                    optionIdx
                  );

                const optionResponse = responses[uniqueOptionQuestionId];
                const isOptionFileUpload = optionQuestion.question_type === "File Input" ||
                                           getInternalType(optionQuestion.question_type) === "file";

                if (isOptionFileUpload) {
                  const file = optionResponse;
                  if (optionQuestion.isRequired && !file) {
                    errors[uniqueOptionQuestionId] = "File is required";
                  } else if (file instanceof File) {
                    if (optionQuestion.allowed_file_type) {
                      const allowedTypes = optionQuestion.allowed_file_type
                        .split(",")
                        .map((t) => t.trim().toLowerCase());
                      const fileExt = file.name.split(".").pop().toLowerCase();
                      if (!allowedTypes.includes(fileExt)) {
                        errors[uniqueOptionQuestionId] = `Allowed file types: ${optionQuestion.allowed_file_type}`;
                      }
                    }
                    if (optionQuestion.max_file_size_mb) {
                      const maxSize = optionQuestion.max_file_size_mb * 1024 * 1024;
                      if (file.size > maxSize) {
                        errors[uniqueOptionQuestionId] = `File must be under ${optionQuestion.max_file_size_mb} MB`;
                      }
                    }
                  }
                } else if (
                  optionQuestion.isRequired &&
                  (!optionResponse ||
                    optionResponse.toString().trim() === "")
                ) {
                  errors[uniqueOptionQuestionId] = "This field is required";
                }
              });
            }
          }
        });
      }
    };

    (formData.questions || []).forEach((item) => {
      if (!item.isGroup || item.isGroup === "No") {
        validateQuestion(item);
      } else {
        (item.questions || []).forEach(validateQuestion);
      }
    });

    return errors;
  };

  const buildOptionForm = () => {
    const optionForms = [];

    (formData?.questions || []).forEach((item) => {
      if (item.isGroup === "Yes" && item.questions) {
        // grouped
        item.questions.forEach((q) => {
          (q.options || []).forEach((opt) => {
            if (opt.option_forms) {
              Object.keys(opt.option_forms).forEach((formType) => {
                if (opt.option_forms[formType] === "Yes") {
                  const entry = buildFormEntry(formType, q, opt, item.group_id);
                  if (entry) optionForms.push(entry);
                }
              });
            }
          });
        });
      } else {
        // standalone
        (item.options || []).forEach((opt) => {
          if (opt.option_forms) {
            Object.keys(opt.option_forms).forEach((formType) => {
              if (opt.option_forms[formType] === "Yes") {
                const entry = buildFormEntry(formType, item, opt, null);
                if (entry) optionForms.push(entry);
              }
            });
          }
        });
      }
    });

    return optionForms;
  };

  const buildFormEntry = (formType, q, opt, groupId) => {
    if (formType === "GAPLD" && gapldData?.pregnancy_status) {
      return {
        form_id: formId,
        patient_id: patientId,
        question_id: q.question_id,
        form_question_id: q.form_question_id,
        group_id: groupId,
        option_id: opt.option_id,
        type: "GAPLD",
        ...gapldData,
      };
    }

    if (formType === "MENOPAUSE" && menopauseData?.menopause_date) {
      return {
        form_id: formId,
        patient_id: patientId,
        question_id: q.question_id,
        form_question_id: q.form_question_id,
        group_id: groupId,
        option_id: opt.option_id,
        type: "MENOPAUSE",
        ...menopauseData,
      };
    }

    if (formType === "MALE" && maleData?.date) {
      return {
        form_id: formId,
        patient_id: patientId,
        question_id: q.question_id,
        form_question_id: q.form_question_id,
        group_id: groupId,
        option_id: opt.option_id,
        type: "MALE",
        symptoms_intercourse: maleData.symptoms ? maleData.symptoms.map((s) => s.value) : [],
        metalic_color: maleData.metallicColor?.value || "",
        other_name: maleData.otherSymptom,
        malechart_date: maleData.date,
      };
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (formData.partner_required === "Yes" && formData.partner_added === "No") {
      const isValidPartner = validatePartnerInfo()

      if (!isValidPartner) {
        setIsSubmitting(false);
        return
      }
    }
    try {
      // Validate form
      const errors = validateForm();
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        showErrorAlert("Please fill in all required fields");
        setIsSubmitting(false);
        return;
      }

      // Build submission payload with answers for all questions
      const allResponses = [];

      // Process all questions in sequence to maintain order
      for (const item of (formData.questions || [])) {
        if (!item.isGroup || item.isGroup === "No") {
          // Handle standalone question
          const isCheckbox = getInternalType(item.question_type) === "checkbox";
          const isFileUpload = item.question_type === "File Input" || 
                               getInternalType(item.question_type) === "file";
          
          let answer = null;
          let fileBase64 = null;
          
          if (isFileUpload) {
            // Handle file upload
            const file = responses[item.form_question_id];
            if (file instanceof File) {
              fileBase64 = await convertFileToBase64(file);
              // Store the base64 string as the answer
              answer = fileBase64;
            } else if (typeof file === 'string' && file) {
              answer = file; // Existing answer (if editing)
            }
          } else {
            answer = Array.isArray(responses[item.form_question_id])
              ? responses[item.form_question_id].join(", ")
              : responses[item.form_question_id] || null;
          }

          if (answer) {
            const responseObj = {
              question_id: item.question_id,
              form_question_id: item.form_question_id,
              answer: answer,
              question: item.question || item.label,
              question_type: item.question_type,
              option_id: isCheckbox
                ? (responses[`${item.form_question_id}_option_ids`] || []).join("|")
                : responses[`${item.form_question_id}_option_id`] || null,
              sequence_order: parseInt(item.orders) || 0,
            };
            
            // Add file metadata if it's a file upload
            if (isFileUpload && responses[item.form_question_id] instanceof File) {
              responseObj.file_name = responses[item.form_question_id].name;
              responseObj.file_size = responses[item.form_question_id].size;
              responseObj.file_type = responses[item.form_question_id].type;
            }
            
            allResponses.push(responseObj);
          }

          // Add option questions responses for standalone questions
          if (item.options && item.options.length > 0) {
            for (const option of item.options) {
              if (option.option_questions && option.option_questions.length > 0) {
                const optionKey = `${item.form_question_id}_${option.option_id}`;
                if (visibleOptionQuestions[optionKey]) {
                  for (const [optionIdx, optionQuestion] of option.option_questions.entries()) {
                    const uniqueOptionQuestionId = generateUniqueOptionQuestionId(
                      item.form_question_id, 
                      option, 
                      optionQuestion, 
                      optionIdx
                    );
                    const optionResponse = responses[uniqueOptionQuestionId];
                    
                    // Check if this is a file upload for option question
                    const isOptionFileUpload = optionQuestion.question_type === "File Input" ||
                                               getInternalType(optionQuestion.question_type) === "file";
                    
                    let optionAnswer = null;
                    let optionFileBase64 = null;
                    
                    if (isOptionFileUpload && optionResponse instanceof File) {
                      optionFileBase64 = await convertFileToBase64(optionResponse);
                      optionAnswer = optionFileBase64;
                    } else {
                      optionAnswer = Array.isArray(optionResponse)
                        ? optionResponse.join(", ")
                        : optionResponse;
                    }
                    
                    if (optionAnswer) {
                      const optionResponseObj = {
                        question_id: optionQuestion.id,
                        answer: optionAnswer,
                        question: optionQuestion.label,
                        parent_question_id: item.form_question_id,
                        parent_option_id: option.option_id,
                        question_type: optionQuestion.question_type,
                        sequence_order: parseInt(item.orders) || 0,
                      };
                      
                      if (isOptionFileUpload && optionResponse instanceof File) {
                        optionResponseObj.file_name = optionResponse.name;
                        optionResponseObj.file_size = optionResponse.size;
                        optionResponseObj.file_type = optionResponse.type;
                      }
                      
                      allResponses.push(optionResponseObj);
                    }
                  }
                }
              }
            }
          }
        } else {
          // Handle grouped questions
          for (const question of (item.questions || [])) {
            const isCheckbox = getInternalType(question.question_type) === "checkbox";
            const isFileUpload = question.question_type === "File Input" ||
                                 getInternalType(question.question_type) === "file";
            
            let answer = null;
            let fileBase64 = null;
            
            if (isFileUpload) {
              const file = responses[question.form_question_id];
              if (file instanceof File) {
                fileBase64 = await convertFileToBase64(file);
                answer = fileBase64;
              } else if (typeof file === 'string' && file) {
                answer = file;
              }
            } else {
              answer = Array.isArray(responses[question.form_question_id])
                ? responses[question.form_question_id].join(", ")
                : responses[question.form_question_id] || null;
            }

            if (answer) {
              const responseObj = {
                question_id: question.question_id,
                form_question_id: question.form_question_id,
                answer: answer,
                question: question.label || question.question,
                group_id: item?.group_id,
                question_type: question.question_type,
                option_id: isCheckbox
                  ? (responses[`${question.form_question_id}_option_ids`] || []).join("|")
                  : responses[`${question.form_question_id}_option_id`] || null,
                sequence_order: parseInt(item.orders) || 0,
              };
              
              if (isFileUpload && responses[question.form_question_id] instanceof File) {
                responseObj.file_name = responses[question.form_question_id].name;
                responseObj.file_size = responses[question.form_question_id].size;
                responseObj.file_type = responses[question.form_question_id].type;
              }
              
              allResponses.push(responseObj);
            }

            // Add option questions responses for grouped questions
            if (question.options && question.options.length > 0) {
              for (const option of question.options) {
                if (option.option_questions && option.option_questions.length > 0) {
                  const optionKey = `${question.form_question_id}_${option.option_id}`;
                  if (visibleOptionQuestions[optionKey]) {
                    for (const [optionIdx, optionQuestion] of option.option_questions.entries()) {
                      const uniqueOptionQuestionId = generateUniqueOptionQuestionId(
                        question.form_question_id,
                        option,
                        optionQuestion,
                        optionIdx
                      );
                      const optionResponse = responses[uniqueOptionQuestionId];
                      
                      const isOptionFileUpload = optionQuestion.question_type === "File Input" ||
                                                 getInternalType(optionQuestion.question_type) === "file";
                      
                      let optionAnswer = null;
                      let optionFileBase64 = null;
                      
                      if (isOptionFileUpload && optionResponse instanceof File) {
                        optionFileBase64 = await convertFileToBase64(optionResponse);
                        optionAnswer = optionFileBase64;
                      } else {
                        optionAnswer = Array.isArray(optionResponse)
                          ? optionResponse.join(", ")
                          : optionResponse;
                      }
                      
                      if (optionAnswer) {
                        const optionResponseObj = {
                          question_id: optionQuestion.id,
                          answer: optionAnswer,
                          question: optionQuestion.label,
                          group_id: item?.group_id,
                          parent_question_id: question.form_question_id,
                          parent_option_id: option.option_id,
                          question_type: optionQuestion.question_type,
                          sequence_order: parseInt(item.orders) || 0,
                        };
                        
                        if (isOptionFileUpload && optionResponse instanceof File) {
                          optionResponseObj.file_name = optionResponse.name;
                          optionResponseObj.file_size = optionResponse.size;
                          optionResponseObj.file_type = optionResponse.type;
                        }
                        
                        allResponses.push(optionResponseObj);
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }

      const optionForms = buildOptionForm();

      const payload = {
        form_id: formId,
        patient_id: patientId,
        ...(formData.partner_required === "Yes" && formData.partner_added !== "Yes"
          ? partnerInfo
          : {}),
        answers: JSON.stringify(allResponses),
        option_form: JSON.stringify(optionForms),
      };

      // Send to backend
      const response = await axiosInstance.post(
        "/admin/addPatientFormAnswer",
        payload
      );
      console.log("API Response:", response.data);

      if (response.data.status === "success") {
        setIsSubmitted(true);
        localStorage.setItem(`form_submitted_${formId}_${patientId}`, "true");
        handleSuccess("Form submitted successfully!");
      } else {
        throw new Error(response.data.message || "Failed to submit form");
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.messages?.error || err.message || "Failed to submit form";
      showErrorAlert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = (question, idx, isGrouped = false, groupSequence = null) => {
    const error = formErrors[question.form_question_id];

    // Create proper sequence numbering
    let questionNumber = "";
    if (isGrouped && groupSequence !== null) {
      questionNumber = `${groupSequence}.${idx + 1}. `;
    } else if (!isGrouped) {
      questionNumber = `${idx + 1}. `;
    }

    const internalType = getInternalType(question.question_type);

    const renderInput = () => {
      const currentValue =
        responses[question.form_question_id] || question.answer || "";

      switch (internalType) {
        case "short_answer":
          return (
            <input
              className={`form-control mt-2 ${error ? "is-invalid" : ""}`}
              type="text"
              value={currentValue}
              onChange={(e) =>
                handleChange(question.form_question_id, e.target.value)
              }
              required={question.is_required === "Yes"}
              placeholder="Enter your answer"
            />
          );

        case "paragraph":
          return (
            <textarea
              className={`form-control mt-2 ${error ? "is-invalid" : ""}`}
              value={currentValue}
              onChange={(e) =>
                handleChange(question.form_question_id, e.target.value)
              }
              required={question.is_required === "Yes"}
              rows={4}
              placeholder="Enter your detailed answer"
            />
          );

        case "multiple_choice":
          return (
            <div className="mt-2">
              {(question.options || []).map((opt, optIndex) => {
                const isSelected = currentValue === opt.option_name;
                const hasOptionQuestions = opt.option_questions && opt.option_questions.length > 0;

                return (
                  <div key={opt.option_id || optIndex}>
                    <div className="form-check">
                      <input
                        className={`form-check-input ${error ? "is-invalid" : ""}`}
                        type="radio"
                        name={`question_${question.form_question_id}`}
                        id={`${question.form_question_id}-${opt.option_id || optIndex}`}
                        value={opt.option_id}
                        checked={isSelected}
                        onChange={(e) =>
                          handleChange(
                            question.form_question_id,
                            opt.option_name,
                            e.target.value || null
                          )
                        }
                        required={question.is_required === "Yes"}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`${question.form_question_id}-${opt.option_id || optIndex}`}
                      >
                        {opt.option_name || opt.label || opt}
                        {hasOptionQuestions && (
                          <span
                            className="badge bg-info ms-2"
                            title={`${opt.option_questions.length} sub-questions`}
                          >
                            {opt.option_questions.length}
                          </span>
                        )}
                      </label>
                    </div>

                    {isSelected && opt.option_forms &&
                      Object.entries(opt.option_forms).map(([key, value]) => {
                        if (value === "Yes") {
                          return (
                            <div key={key} className="alert alert-info mb-2">
                              <h5>{key} Form</h5>
                              {key === "GAPLD" && <GapldFormFields formData={gapldData} setFormData={setGapldData} />}
                              {key === "MENOPAUSE" && <MenopauseFormFields formData={menopauseData} setFormData={setMenopauseData} />}
                              {key === "MALE" && <MaleFormFields formData={maleData} setFormData={setMaleData} />}
                            </div>
                          );
                        }
                        return null;
                      })}

                    {isSelected && hasOptionQuestions && (
                      <div
                        className="ms-4 mt-2 mb-3 p-3 bg-light rounded"
                        style={{ borderLeft: "3px solid #007bff" }}
                      >
                        {opt.option_questions.map((optionQuestion, optionIdx) => {
                          const uniqueOptionQuestionId = generateUniqueOptionQuestionId(
                            question.form_question_id,
                            opt,
                            optionQuestion,
                            optionIdx
                          );

                          return (
                            <div key={uniqueOptionQuestionId} className="mb-3">
                              <label className="form-label">
                                <strong>{optionQuestion.label}</strong>
                                {optionQuestion.isRequired && (
                                  <span className="text-danger"> *</span>
                                )}
                              </label>
                              {formErrors[uniqueOptionQuestionId] && (
                                <div className="text-danger small">
                                  {formErrors[uniqueOptionQuestionId]}
                                </div>
                              )}
                              {renderOptionQuestionInput(
                                optionQuestion,
                                uniqueOptionQuestionId
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );

        case "checkbox":
          return (
            <div className="mt-2">
              {(question.options || []).map((opt, optIndex) => {
                const optionValue = opt.option_name;
                const optionId = opt.option_id;
                const currentValues = Array.isArray(currentValue)
                  ? currentValue
                  : currentValue
                    ? [currentValue]
                    : [];
                const currentOptionIds = Array.isArray(
                  responses[`${question.form_question_id}_option_ids`]
                )
                  ? responses[`${question.form_question_id}_option_ids`]
                  : [];

                const isSelected = currentValues.includes(optionValue);
                const hasOptionQuestions = opt.option_questions && opt.option_questions.length > 0;

                return (
                  <div key={optionId || optIndex}>
                    <div className="form-check">
                      <input
                        className={`form-check-input ${error ? "is-invalid" : ""}`}
                        type="checkbox"
                        name={`question_${question.form_question_id}`}
                        id={`${question.form_question_id}-${optionId || optIndex}`}
                        value={optionValue}
                        checked={isSelected}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const newValues = checked
                            ? [...currentValues, optionValue]
                            : currentValues.filter((v) => v !== optionValue);
                          const newOptionIds = checked
                            ? [...currentOptionIds, optionId]
                            : currentOptionIds.filter((id) => id !== optionId);

                          handleChange(question.form_question_id, newValues, optionId);
                          setResponses((prev) => ({
                            ...prev,
                            [`${question.form_question_id}_option_ids`]: newOptionIds,
                          }));
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`${question.form_question_id}-${optionId || optIndex}`}
                      >
                        {opt.option_name || opt.label || opt}
                        {hasOptionQuestions && (
                          <span className="badge bg-info ms-2" title={`${opt.option_questions.length} sub-questions`}>
                            {opt.option_questions.length}
                          </span>
                        )}
                      </label>
                    </div>

                    {isSelected && hasOptionQuestions && (
                      <div className="ms-4 mt-2 mb-3 p-3 bg-light rounded" style={{ borderLeft: "3px solid #007bff" }}>
                        {opt.option_questions.map((optionQuestion, optionIdx) => {
                          const uniqueOptionQuestionId = generateUniqueOptionQuestionId(question.form_question_id, opt, optionQuestion, optionIdx);
                          return (
                            <div key={uniqueOptionQuestionId} className="mb-3">
                              <label className="form-label">
                                <strong>{optionQuestion.label}</strong>
                                {optionQuestion.isRequired && (
                                  <span className="text-danger"> *</span>
                                )}
                              </label>
                              {formErrors[uniqueOptionQuestionId] && (
                                <div className="text-danger small">{formErrors[uniqueOptionQuestionId]}</div>
                              )}
                              {renderOptionQuestionInput(optionQuestion, uniqueOptionQuestionId)}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );

        case "dropdown":
          return (
            <div className="mt-2">
              <select
                className={`form-select ${error ? "is-invalid" : ""}`}
                value={currentValue}
                onChange={(e) => {
                  const selectedOpt = (question.options || []).find(
                    (opt) =>
                      String(opt.option_id || opt.option_name) === e.target.value
                  );
                  handleChange(
                    question.form_question_id,
                    selectedOpt ? selectedOpt.option_name : e.target.value,
                    selectedOpt ? selectedOpt.option_id : null
                  );
                }}
                required={question.is_required === "Yes"}
              >
                <option value="">Select an option</option>
                {(question.options || []).map((opt, optIndex) => (
                  <option
                    key={opt.option_id || optIndex}
                    value={opt.option_id || opt.option_name}
                  >
                    {opt.option_name || opt.label || opt}
                  </option>
                ))}
              </select>

              {question.options &&
                question.options.map((opt) => {
                  const isSelected =
                    currentValue === (opt.option_name || String(opt.option_id));
                  if (isSelected && opt.option_forms) {
                    return (
                      <div key={opt.option_id} className="mt-3">
                        {Object.entries(opt.option_forms).map(([key, value]) => {
                          if (value === "Yes") {
                            return (
                              <div key={key} className="alert alert-info mb-2">
                                <h5>{key} Form</h5>
                                {key === "GAPLD" && <GapldFormFields formData={gapldData} setFormData={setGapldData} />}
                                {key === "MENOPAUSE" && <MenopauseFormFields formData={menopauseData} setFormData={setMenopauseData} />}
                                {key === "MALE" && <MaleFormFields formData={maleData} setFormData={setMaleData} />}
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    );
                  }
                  return null;
                })}
            </div>
          );

        case "file":
        case "file_input":
        case "File Input":
          return (
            <div className="mt-2">
              <input
                type="file"
                className={`form-control ${error ? "is-invalid" : ""}`}
                accept={
                  question.allowed_file_type
                    ? question.allowed_file_type
                      .split(",")
                      .map((t) => `.${t.trim()}`)
                      .join(",")
                    : "*"
                }
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;

                  const allowedTypes = question.allowed_file_type
                    ?.split(",")
                    .map((t) => t.trim().toLowerCase());

                  const fileExt = file.name.split(".").pop().toLowerCase();

                  if (allowedTypes && !allowedTypes.includes(fileExt)) {
                    showErrorAlert(`File type not allowed. Allowed types: ${question.allowed_file_type}`);
                    e.target.value = "";
                    return;
                  }

                  if (question.max_file_size_mb) {
                    const maxSize = question.max_file_size_mb * 1024 * 1024;
                    if (file.size > maxSize) {
                      showErrorAlert(`File size exceeds ${question.max_file_size_mb} MB limit`);
                      e.target.value = "";
                      return;
                    }
                  }

                  handleChange(question.form_question_id, file);
                }}
                required={question.is_required === "Yes"}
              />

              {question.allowed_file_type && (
                <small className="text-muted d-block">
                  Allowed: {question.allowed_file_type}
                </small>
              )}

              {question.max_file_size_mb && (
                <small className="text-muted d-block">
                  Max Size: {question.max_file_size_mb} MB
                </small>
              )}

              {responses[question.form_question_id] instanceof File && (
                <div className="text-success small mt-1">
                  Selected: {responses[question.form_question_id].name}
                </div>
              )}
            </div>
          );

        case "date":
          return (
            <input
              className={`form-control mt-2 ${error ? "is-invalid" : ""}`}
              type="date"
              value={currentValue}
              onChange={(e) =>
                handleChange(question.form_question_id, e.target.value)
              }
              required={question.is_required === "Yes"}
            />
          );

        case "time":
          return (
            <div className="input-group mt-2">
              <input
                className={`form-control ${error ? "is-invalid" : ""}`}
                type="time"
                value={currentValue}
                onChange={(e) =>
                  handleChange(question.form_question_id, e.target.value)
                }
                required={question.is_required === "Yes"}
              />
              <span className="input-group-text">
                <i className="far fa-clock"></i>
              </span>
            </div>
          );

        default:
          return (
            <input
              className={`form-control mt-2 ${error ? "is-invalid" : ""}`}
              type="text"
              value={currentValue}
              onChange={(e) =>
                handleChange(question.form_question_id, e.target.value)
              }
              required={question.is_required === "Yes"}
              placeholder="Enter your answer"
            />
          );
      }
    };

    const renderOptionQuestionInput = (optionQuestion, uniqueId = null) => {
      const questionId = uniqueId || optionQuestion.id;
      const currentValue = responses[questionId] || "";
      const internalType = getInternalType(optionQuestion.question_type);
      const isFileUpload = optionQuestion.question_type === "File Input" ||
                           getInternalType(optionQuestion.question_type) === "file";

      if (isFileUpload) {
        return (
          <div className="mt-2">
            <input
              type="file"
              className={`form-control ${formErrors[questionId] ? "is-invalid" : ""}`}
              accept={
                optionQuestion.allowed_file_type
                  ? optionQuestion.allowed_file_type
                      .split(",")
                      .map((t) => `.${t.trim()}`)
                      .join(",")
                  : "*"
              }
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;

                const allowedTypes = optionQuestion.allowed_file_type
                  ?.split(",")
                  .map((t) => t.trim().toLowerCase());

                const fileExt = file.name.split(".").pop().toLowerCase();

                if (allowedTypes && !allowedTypes.includes(fileExt)) {
                  showErrorAlert(`File type not allowed. Allowed types: ${optionQuestion.allowed_file_type}`);
                  e.target.value = "";
                  return;
                }

                if (optionQuestion.max_file_size_mb) {
                  const maxSize = optionQuestion.max_file_size_mb * 1024 * 1024;
                  if (file.size > maxSize) {
                    showErrorAlert(`File size exceeds ${optionQuestion.max_file_size_mb} MB limit`);
                    e.target.value = "";
                    return;
                  }
                }

                handleChange(questionId, file);
              }}
              required={optionQuestion.isRequired}
            />
            {optionQuestion.allowed_file_type && (
              <small className="text-muted d-block">
                Allowed: {optionQuestion.allowed_file_type}
              </small>
            )}
            {optionQuestion.max_file_size_mb && (
              <small className="text-muted d-block">
                Max Size: {optionQuestion.max_file_size_mb} MB
              </small>
            )}
            {responses[questionId] instanceof File && (
              <div className="text-success small mt-1">
                Selected: {responses[questionId].name}
              </div>
            )}
          </div>
        );
      }

      switch (internalType) {
        case "short_answer":
          return (
            <input
              className={`form-control mt-2 ${formErrors[questionId] ? "is-invalid" : ""}`}
              type="text"
              value={currentValue}
              onChange={(e) =>
                handleChange(questionId, e.target.value)
              }
              required={optionQuestion.isRequired}
              placeholder="Enter your answer"
            />
          );

        case "paragraph":
          return (
            <textarea
              className={`form-control mt-2 ${formErrors[questionId] ? "is-invalid" : ""}`}
              value={currentValue}
              onChange={(e) =>
                handleChange(questionId, e.target.value)
              }
              required={optionQuestion.isRequired}
              rows={3}
              placeholder="Enter your detailed answer"
            />
          );

        case "multiple_choice":
          return (
            <div className="mt-2">
              {(optionQuestion.options || []).map((opt, optIndex) => {
                return (
                  <div key={opt.option_id || optIndex} className="form-check">
                    <input
                      className={`form-check-input ${formErrors[questionId] ? "is-invalid" : ""}`}
                      type="radio"
                      name={`option_question_${questionId}`}
                      id={`${questionId}-${opt.option_id || optIndex}`}
                      value={opt.option_name || opt.value || opt.label}
                      checked={currentValue === (opt.option_name || opt.value || opt.label)}
                      onChange={(e) =>
                        handleChange(questionId, e.target.value)
                      }
                      required={optionQuestion.isRequired}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`${questionId}-${opt.option_id || optIndex}`}
                    >
                      {opt.option_name || opt.label}
                    </label>
                  </div>
                );
              })}
            </div>
          );

        case "checkbox":
          return (
            <div className="mt-2">
              {(optionQuestion.options || []).map((opt, optIndex) => {
                const currentValues = Array.isArray(currentValue)
                  ? currentValue
                  : currentValue
                    ? [currentValue]
                    : [];

                return (
                  <div key={opt.option_id || optIndex} className="form-check">
                    <input
                      className={`form-check-input ${formErrors[questionId] ? "is-invalid" : ""}`}
                      type="checkbox"
                      name={`option_question_${questionId}`}
                      id={`${questionId}-${opt.option_id || optIndex}`}
                      value={opt.option_name || opt.label}
                      checked={currentValues.includes(opt.option_name || opt.label)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const optionValue = opt.option_name || opt.label;
                        const newValues = checked
                          ? [...currentValues, optionValue]
                          : currentValues.filter((v) => v !== optionValue);

                        setResponses((prev) => ({
                          ...prev,
                          [questionId]: newValues,
                        }));

                        if (formErrors[questionId]) {
                          setFormErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors[questionId];
                            return newErrors;
                          });
                        }
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`${questionId}-${opt.option_id || optIndex}`}
                    >
                      {opt.option_name || opt.label}
                    </label>
                  </div>
                );
              })}
            </div>
          );

        case "date":
          return (
            <input
              className={`form-control mt-2 ${formErrors[questionId] ? "is-invalid" : ""}`}
              type="date"
              value={currentValue}
              onChange={(e) =>
                handleChange(questionId, e.target.value)
              }
              required={optionQuestion.isRequired}
            />
          );

        case "time":
          return (
            <div className="input-group mt-2">
              <input
                className={`form-control ${formErrors[questionId] ? "is-invalid" : ""}`}
                type="time"
                value={currentValue}
                onChange={(e) =>
                  handleChange(questionId, e.target.value)
                }
                required={optionQuestion.isRequired}
              />
              <span className="input-group-text">
                <i className="far fa-clock"></i>
              </span>
            </div>
          );

        default:
          return (
            <input
              className={`form-control mt-2 ${formErrors[questionId] ? "is-invalid" : ""}`}
              type="text"
              value={currentValue}
              onChange={(e) =>
                handleChange(questionId, e.target.value)
              }
              required={optionQuestion.isRequired}
              placeholder="Enter your answer"
            />
          );
      }
    };

    return (
      <div
        key={question.form_question_id}
        className={`mb-4 ${isGrouped ? "ms-3" : ""}`}
      >
        <label className="form-label">
          {questionNumber && (
            <strong>{questionNumber}</strong>
          )}
          <strong>
            {question.label || question.question}
          </strong>
          {question.is_required === "Yes" && (
            <span className="text-danger"> *</span>
          )}
          {question.description && (
            <small className="text-muted d-block">{question.description}</small>
          )}
        </label>
        {error && <div className="text-danger small">{error}</div>}
        {renderInput()}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Loader size="lg" />
        <p className="mt-3">Loading form...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger text-center mt-5">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </div>
    );
  }

  if (!formData) {
    return null;
  }

  if (formData && formData.form_submitted == "Yes") {
    return <FormAlreadySubmitted />;
  }

  if (isSubmitted) {
    return (
      <FormSuccessMessage
        title="Form submitted successfully. Thank you!"
        message="Your response has been recorded successfully."
      />
    );
  }

  if (loading) return <Loader />;

  if (!formData) return null;

  if (formData.form_submitted === "Yes") {
    return <FormAlreadySubmitted />;
  }

  if (isSubmitted) {
    return (
      <FormSuccessMessage
        title="Form submitted successfully. Thank you!"
        message="Your response has been recorded successfully."
      />
    );
  }

  const unifiedFormItems = [];

  (formData.questions || []).forEach((item, index) => {
    if (!item.isGroup || item.isGroup === "No") {
      unifiedFormItems.push({
        type: "single",
        content: item,
        orders: parseInt(item.orders) || index,
        originalIndex: index
      });
    } else if (item.isGroup === "Yes") {
      const sortedGroupQuestions = (item.questions || []).sort((a, b) => {
        const orderA = parseInt(a.orders) || 0;
        const orderB = parseInt(b.orders) || 0;
        return orderA - orderB;
      });

      unifiedFormItems.push({
        type: "group",
        content: {
          ...item,
          questions: sortedGroupQuestions
        },
        orders: parseInt(item.orders) || index,
        originalIndex: index
      });
    }
  });

  unifiedFormItems.sort((a, b) => a.orders - b.orders);

  return (
    <Fragment>
      <div className="h-100">
        <div className="container h-100">
          <div className="row justify-content-center h-100 align-items-center">
            <div className="col-md-12 col-lg-6"></div>

            <div className="row justify-content-center align-items-center">
              <div className="col-md-12 col-lg-6 align-items-center">
                <img
                  src={logo}
                  alt="Logo"
                  style={{ width: "100%", height: "auto" }}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-xl-12 col-xxl-12">
                <div className="card">
                  <div className="card-body">
                    <h2 className="card-title">{formData.form_title}</h2>
                    <p className="card-text">{formData.form_description}</p>
                    {formData.form_image && (
                      <div className="text-center mb-4">
                        <img
                          src={formData.form_image}
                          alt="Form Header"
                          style={{ maxWidth: "30%", height: "auto" }}
                        />
                      </div>
                    )}

                    <form onSubmit={handleSubmit}>
                      {formData.partner_required === "Yes" && formData.partner_added === "No" && (
                        <div className="card mt-3">
                          <div className="card-body">
                            <h4 className="mb-3">Partner Information</h4>
                            <div className="row">
                              <div className="col-md-4 mb-3">
                                <label>First Name</label><span style={{ color: "red" }}>*</span>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="first_name"
                                  value={partnerInfo.first_name}
                                  onChange={handlePartnerChange}
                                />
                                {partnerErrors.first_name && <small className="text-danger">{partnerErrors.first_name}</small>}
                              </div>

                              <div className="col-md-4 mb-3">
                                <label>Middle Name</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="middle_name"
                                  value={partnerInfo.middle_name}
                                  onChange={handlePartnerChange}
                                />
                              </div>

                              <div className="col-md-4 mb-3">
                                <label>Last Name</label>
                                <span style={{ color: "red" }}>*</span>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="last_name"
                                  value={partnerInfo.last_name}
                                  onChange={handlePartnerChange}
                                />
                                {partnerErrors.last_name && <small className="text-danger">{partnerErrors.last_name}</small>}
                              </div>

                              <div className="col-md-4 mb-3">
                                <label>Email</label>
                                <span style={{ color: "red" }}>*</span>
                                <input
                                  type="email"
                                  className="form-control"
                                  name="email"
                                  value={partnerInfo.email}
                                  onChange={handlePartnerChange}
                                />
                                {partnerErrors.email && <small className="text-danger">{partnerErrors.email}</small>}
                              </div>

                              <div className="col-md-4 mb-3">
                                <label>Mobile No</label>
                                <span style={{ color: "red" }}>*</span>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="mobile_no"
                                  value={partnerInfo.mobile_no}
                                  onChange={handlePartnerChange}
                                  maxLength="10"
                                />
                                {partnerErrors.mobile_no && <small className="text-danger">{partnerErrors.mobile_no}</small>}
                              </div>

                              <div className="col-md-4 mb-3">
                                <label>Alternate Mobile</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="alternate_mobile_no"
                                  value={partnerInfo.alternate_mobile_no}
                                  onChange={handlePartnerChange}
                                  maxLength="10"
                                />
                                {partnerErrors.alternate_mobile_no && (
                                  <small className="text-danger">{partnerErrors.alternate_mobile_no}</small>
                                )}
                              </div>

                              <div className="col-md-4 mb-3">
                                <label>Date of Birth</label>
                                <span style={{ color: "red" }}>*</span>
                                <input
                                  type="date"
                                  className="form-control"
                                  name="date_of_birth"
                                  value={partnerInfo.date_of_birth}
                                  onChange={handlePartnerChange}
                                  max={new Date().toISOString().split("T")[0]}
                                />
                                {partnerErrors.date_of_birth && <small className="text-danger">{partnerErrors.date_of_birth}</small>}
                              </div>

                              <div className="col-xl-4 mb-3">
                                <label>
                                  Gender<span style={{ color: "red" }}>*</span>
                                </label>
                                <Select
                                  name="gender"
                                  options={[
                                    { value: "Male", label: "Male" },
                                    { value: "Female", label: "Female" },
                                    { value: "Transgender", label: "Transgender" },
                                  ]}
                                  value={
                                    partnerInfo.gender
                                      ? { value: partnerInfo.gender, label: partnerInfo.gender }
                                      : null
                                  }
                                  onChange={handleGenderChange}
                                  placeholder="Select Gender"
                                  styles={{
                                    control: (provided) => ({
                                      ...provided,
                                      minHeight: "50px",
                                      height: "50px",
                                    }),
                                    singleValue: (provided) => ({
                                      ...provided,
                                      lineHeight: "50px",
                                    }),
                                    dropdownIndicator: (provided) => ({
                                      ...provided,
                                      padding: "4px",
                                    }),
                                    indicatorSeparator: (provided) => ({
                                      ...provided,
                                      display: "none",
                                    }),
                                  }}
                                />
                                {partnerErrors.gender && <small className="text-danger">{partnerErrors.gender}</small>}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {unifiedFormItems.length > 0 && (
                        <>
                          <h5>Form Questions:</h5>
                          {unifiedFormItems.map((item, idx) => {
                            if (item.type === "single") {
                              return renderQuestion(item.content, idx, false);
                            } else if (item.type === "group") {
                              return (
                                <div key={item.content.group_id} className="mb-4">
                                  <div className="card"
                                    style={{
                                      backgroundColor: item.content.bg_color
                                    }}>
                                    <div className="card-body">
                                      <h4 className="border-bottom pb-2">
                                        <strong>{idx + 1}.</strong> {item.content.label || `Group ${item.content.group_id}`}
                                      </h4>
                                      {item.content.questions &&
                                        item.content.questions.length > 0 ? (
                                        item.content.questions.map((question, qIdx) =>
                                          renderQuestion(question, qIdx, true, idx + 1)
                                        )
                                      ) : (
                                        <p>No questions in this group</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })}
                        </>
                      )}

                      <div className="alert alert-warning mt-4">
                        <strong>Disclaimer:</strong> Meet the doctor at the earliest because your medical history can be changed.
                      </div>
                      
                      {patientId && (
                        <div className="d-flex justify-content-end mt-4">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <>
                                <Loader size="sm" />
                                <span className="ms-2">Submitting...</span>
                              </>
                            ) : (
                              "Submit"
                            )}
                          </button>
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default PatientForm;